import { useEffect, useRef } from 'react';
import { testimonials } from '../../../mocks/testimonials';

function StarRating({ count }: { count: number }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: count }).map((_, i) => (
        <i key={i} className="ri-star-fill" style={{ color: '#C9A96E', fontSize: '0.75rem' }} />
      ))}
    </div>
  );
}

function TestimonialCard({ testimonial, delay }: { testimonial: typeof testimonials[0]; delay: number }) {
  return (
    <div
      data-animate
      className="bg-white rounded-lg p-7 flex flex-col gap-5"
      style={{
        opacity: 0,
        transform: 'translateY(28px)',
        transition: `all 0.7s ease ${delay}s`,
      }}
    >
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 border-2 border-amber-100">
          <img src={testimonial.avatar} alt={testimonial.name} className="w-full h-full object-cover object-top" />
        </div>
        <div>
          <p
            className="text-stone-800"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, fontSize: '1.2rem' }}
          >
            {testimonial.name}
          </p>
        </div>
        <div className="ml-auto">
          <span
            className="px-2.5 py-1 rounded-sm"
            style={{
              background: '#FBF5EC',
              color: '#A07840',
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '0.75rem',
              letterSpacing: '0.08em',
            }}
          >
            {testimonial.tier}
          </span>
        </div>
      </div>

      {/* Rating */}
      <StarRating count={testimonial.rating} />

      {/* Quote */}
      <p
        className="text-stone-600 leading-relaxed flex-1"
        style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic', fontWeight: 400, fontSize: '1.2rem', lineHeight: 1.7 }}
      >
        &ldquo;{testimonial.quote}&rdquo;
      </p>

      {/* Date */}
      <div className="flex items-center gap-2 pt-2 border-t border-stone-100">
        <i className="ri-heart-line" style={{ color: '#C9A96E', fontSize: '0.85rem' }} />
        <p
          className="text-stone-400"
          style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: '0.85rem' }}
        >
          Married {testimonial.marriedDate}
        </p>
      </div>
    </div>
  );
}

export default function TestimonialsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('[data-animate]').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="testimonials" ref={sectionRef} className="py-28 px-6 lg:px-12 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-16" data-animate style={{ opacity: 0, transform: 'translateY(24px)', transition: 'all 0.7s ease' }}>
          <p
            className="inline-block tracking-widest uppercase px-4 py-1.5 border border-amber-300/60 text-amber-700 mb-6 rounded-sm"
            style={{ fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.18em', fontSize: '0.8rem' }}
          >
            Love Stories
          </p>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2
              className="text-5xl lg:text-6xl text-stone-800"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, lineHeight: 1.15 }}
            >
              Brides Who Created<br />
              <em style={{ fontStyle: 'italic' }}>Their Dream Gown</em>
            </h2>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-4xl text-amber-600" style={{ fontFamily: "'Cormorant Garamond', serif" }}>★ 5.0</p>
                <p className="text-sm text-stone-400 mt-1" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>Average Rating</p>
              </div>
              <div className="w-px h-10 bg-stone-200" />
              <div className="text-center">
                <p className="text-4xl text-amber-600" style={{ fontFamily: "'Cormorant Garamond', serif" }}>127</p>
                <p className="text-sm text-stone-400 mt-1" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>Reviews</p>
              </div>
            </div>
          </div>
        </div>

        {/* Testimonials Grid - 3 columns */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-start">
          {testimonials.map((t, i) => (
            <TestimonialCard key={t.id} testimonial={t} delay={i * 0.15 + 0.1} />
          ))}
        </div>
      </div>
    </section>
  );
}
